import type { PartnerCalendarEntry } from '../types';

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function ymdAddDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const nextYmd = (ymd: string) => ymdAddDays(ymd, 1);

/** Every YYYY-MM-DD the entry covers, inclusive of both boundary days. Capped at one year. */
export function expandEntryDates(entry: Pick<PartnerCalendarEntry, 'startDate' | 'endDate'>): string[] {
  const start = String(entry.startDate || '').slice(0, 10);
  if (!YMD_PATTERN.test(start)) return [];
  const rawEnd = String(entry.endDate || '').slice(0, 10);
  const end = YMD_PATTERN.test(rawEnd) && rawEnd >= start ? rawEnd : start;
  const dates: string[] = [];
  let cursor = start;
  while (cursor <= end && dates.length < 366) {
    dates.push(cursor);
    cursor = nextYmd(cursor);
  }
  return dates;
}

/** True when a no-class entry applies to the given squad location. */
export function entryAppliesToLocation(entry: PartnerCalendarEntry, locationId: string | null | undefined): boolean {
  const excluded = Array.isArray(entry.excludedLocationIds) ? entry.excludedLocationIds : [];
  const id = String(locationId || '').trim();
  return !id || !excluded.includes(id);
}

/**
 * Sorted, deduplicated no-class dates a squad at the given location inherits
 * from the partner calendar.
 */
export function inheritedNoClassDatesForLocation(
  entries: PartnerCalendarEntry[],
  locationId: string | null | undefined,
): string[] {
  const dates = new Set<string>();
  for (const entry of entries) {
    if (entry.kind !== 'no_class') continue;
    if (!entryAppliesToLocation(entry, locationId)) continue;
    for (const date of expandEntryDates(entry)) dates.add(date);
  }
  return Array.from(dates).sort();
}

export function sameDateList(left: string[] | undefined, right: string[]): boolean {
  const a = Array.from(new Set((left || []).filter(Boolean))).sort();
  if (a.length !== right.length) return false;
  return a.every((value, index) => value === right[index]);
}

/** Recurring, non-canceled squads are the only ones that inherit partner no-class days. */
export function squadInheritsNoClassDays(squad: {
  scheduleKind?: string;
  schedule?: { mainDate?: string };
  status?: string;
}): boolean {
  if (squad.scheduleKind === 'single_day' || squad.schedule?.mainDate) return false;
  return !['canceled', 'cancelled'].includes(String(squad.status || '').trim().toLowerCase());
}
